import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowLeft, Car, User, Phone, Calendar, ClipboardList, 
  FileText, Camera, CheckCircle2, Sparkles, Loader2, AlertCircle,
  ChevronRight, ArrowRight, Truck, Wrench, ClipboardCheck, Package, MapPin, Clock, Lock, Pencil
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Walk-in workflow stages (8 stages)
const WALKIN_STAGES = [
  { key: 'job_created', label: 'Job Created', icon: Car, color: 'blue' },
  { key: 'ongoing', label: 'Ongoing', icon: Wrench, color: 'purple' },
  { key: 'quote_raised', label: 'Quote Raised', icon: FileText, color: 'yellow' },
  { key: 'quote_approved', label: 'Quote Approved', icon: CheckCircle2, color: 'green' },
  { key: 'work_in_progress', label: 'Work in Progress', icon: Wrench, color: 'orange' },
  { key: 'ready_for_delivery', label: 'Ready for Delivery', icon: Package, color: 'cyan' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2, color: 'emerald' },
  { key: 'closed', label: 'Closed', icon: Lock, color: 'slate' },
];

// Pickup workflow stages (11 stages - 3 extra at beginning)
const PICKUP_STAGES = [
  { key: 'driver_assigned', label: 'Driver Assigned', icon: User, color: 'blue' },
  { key: 'picked_up', label: 'Picked Up', icon: Truck, color: 'blue' },
  { key: 'reached_garage', label: 'Reached Garage', icon: Car, color: 'indigo' },
  { key: 'job_created', label: 'Job Created', icon: ClipboardList, color: 'purple' },
  { key: 'ongoing', label: 'Ongoing', icon: Wrench, color: 'purple' },
  { key: 'quote_raised', label: 'Quote Raised', icon: FileText, color: 'yellow' },
  { key: 'quote_approved', label: 'Quote Approved', icon: CheckCircle2, color: 'green' },
  { key: 'work_in_progress', label: 'Work in Progress', icon: Wrench, color: 'orange' },
  { key: 'ready_for_delivery', label: 'Ready for Delivery', icon: Package, color: 'cyan' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2, color: 'emerald' },
  { key: 'closed', label: 'Closed', icon: Lock, color: 'slate' },
];

// Status aliases for display mapping
const STATUS_ALIASES = {
  'customer_approved': 'quote_approved',
  'customer_partial_approved': 'quote_approved',
  'estimate_sent': 'quote_raised',
  'pending_estimate': 'ongoing',
  'drop_scheduled': 'delivered',
  'dropped': 'delivered',
};

// Get stages based on job type
const getStagesForJob = (jobType) => {
  return jobType === 'pickup' ? PICKUP_STAGES : WALKIN_STAGES;
};

// Normalize status for display
const normalizeStatus = (status) => {
  return STATUS_ALIASES[status] || status;
};

const JobDetail = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { theme } = useTheme();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [generatingChecklist, setGeneratingChecklist] = useState(false);
  const [closingJob, setClosingJob] = useState(false);
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [customerForm, setCustomerForm] = useState({ customer_name: '', customer_mobile: '', customer_email: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);
  
  // Pickup/Drop state
  const [drivers, setDrivers] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [pickupForm, setPickupForm] = useState({ driver_id: '', scheduled_date: '', time_slot: '', notes: '' });
  const [dropForm, setDropForm] = useState({ driver_id: '', scheduled_date: '', time_slot: '', notes: '' });
  const [assigningPickup, setAssigningPickup] = useState(false);
  const [assigningDrop, setAssigningDrop] = useState(false);
  
  // Workflow state
  const [workflow, setWorkflow] = useState(null);
  const [showBackwardConfirm, setShowBackwardConfirm] = useState(false);
  const [pendingBackwardStatus, setPendingBackwardStatus] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState(null); // 'self_pickup' or 'need_drop'

  useEffect(() => {
    const token = localStorage.getItem('garage_token');
    if (!token) {
      navigate('/garage/login');
      return;
    }
    fetchJob();
    fetchDriversAndSlots();
  }, [jobId, navigate]);
  
  const fetchDriversAndSlots = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const [driversRes, slotsRes] = await Promise.all([
        fetch(`${API_URL}/api/garage/drivers/`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/garage/drivers/time-slots`)
      ]);
      const driversData = await driversRes.json();
      const slotsData = await slotsRes.json();
      if (driversData.success) setDrivers(driversData.drivers);
      if (slotsData.success) setTimeSlots(slotsData.time_slots);
    } catch (error) {
      console.error('Failed to fetch drivers/slots:', error);
    }
  };
  
  const handleAssignPickup = async () => {
    if (!pickupForm.driver_id || !pickupForm.scheduled_date || !pickupForm.time_slot) {
      toast.error('Please fill all required fields');
      return;
    }
    setAssigningPickup(true);
    const token = localStorage.getItem('garage_token');
    try {
      const res = await fetch(`${API_URL}/api/garage/drivers/assign-pickup/${jobId}/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pickupForm, assignment_type: 'pickup' })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Driver assigned for pickup! Customer will be notified.');
        fetchJob();
      } else {
        toast.error(data.detail || 'Failed to assign driver');
      }
    } catch (error) {
      toast.error('Failed to assign driver');
    } finally {
      setAssigningPickup(false);
    }
  };
  
  const handleAssignDrop = async () => {
    if (!dropForm.driver_id || !dropForm.scheduled_date || !dropForm.time_slot) {
      toast.error('Please fill all required fields');
      return;
    }
    setAssigningDrop(true);
    const token = localStorage.getItem('garage_token');
    try {
      const res = await fetch(`${API_URL}/api/garage/drivers/assign-drop/${jobId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dropForm, assignment_type: 'drop' })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Driver assigned for delivery! Customer will be notified.');
        fetchJob();
      } else {
        toast.error(data.detail || 'Failed to assign driver');
      }
    } catch (error) {
      toast.error('Failed to assign driver');
    } finally {
      setAssigningDrop(false);
    }
  };

  const fetchJob = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setJob(data.job);
        // Fetch workflow for this job
        fetchWorkflow(data.job.job_type || 'walk_in');
      } else {
        toast.error('Job not found');
        navigate('/garage/jobs');
      }
    } catch (error) {
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflow = async (jobType) => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/workflow`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setWorkflow(data);
      }
    } catch (error) {
      console.error('Failed to fetch workflow:', error);
    }
  };

  const openEditCustomer = () => {
    setCustomerForm({
      customer_name: job?.customer_name || '',
      customer_mobile: job?.customer_mobile || '',
      customer_email: job?.customer_email || '',
    });
    setEditCustomerOpen(true);
  };

  const saveCustomer = async () => {
    if (!customerForm.customer_name.trim() || !customerForm.customer_mobile.trim()) {
      toast.error('Name and mobile are required');
      return;
    }
    setSavingCustomer(true);
    const token = localStorage.getItem('garage_token');
    try {
      const res = await fetch(`${API_URL}/api/garage/jobs/${jobId}/customer`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(customerForm),
      });
      const data = await res.json();
      if (data.success) {
        setJob(data.job);
        setEditCustomerOpen(false);
        toast.success('Customer details updated');
      } else {
        toast.error(data.detail || 'Could not update customer');
      }
    } catch (e) {
      toast.error('Network error — try again');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleStatusUpdate = async (newStatus, overrideBackward = false) => {
    setUpdatingStatus(true);
    const token = localStorage.getItem('garage_token');

    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: newStatus,
          override_backward: overrideBackward
        })
      });
      const data = await response.json();

      if (data.success) {
        toast.success(`Status updated to ${getStatusLabel(newStatus)}`);
        if (data.synced_to_order) {
          toast.info('Order status synced with admin panel');
        }
        setShowBackwardConfirm(false);
        setPendingBackwardStatus(null);
        fetchJob();
      } else {
        // Check if backward move requires confirmation
        if (data.detail && data.detail.includes('requires confirmation')) {
          setPendingBackwardStatus(newStatus);
          setShowBackwardConfirm(true);
        } else {
          toast.error(data.detail || 'Failed to update status');
        }
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConfirmBackward = () => {
    if (pendingBackwardStatus) {
      handleStatusUpdate(pendingBackwardStatus, true);
    }
  };

  const handleCancelBackward = () => {
    setShowBackwardConfirm(false);
    setPendingBackwardStatus(null);
  };

  const handleCloseJob = async () => {
    // Check if payment is received
    if (!job.payment_received) {
      toast.error('Payment must be recorded before closing the job');
      return;
    }
    
    setClosingJob(true);
    const token = localStorage.getItem('garage_token');

    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/close`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Job closed successfully!');
        if (data.feedback_email_sent) {
          toast.info('Feedback request sent to customer');
        }
        fetchJob();
      } else {
        toast.error(data.detail || 'Failed to close job');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setClosingJob(false);
    }
  };

  const handleGenerateChecklist = async () => {
    if (!job.customer_complaint?.text) {
      toast.error('Add customer complaint first');
      return;
    }

    setGeneratingChecklist(true);
    const token = localStorage.getItem('garage_token');

    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/generate-checklist`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Checklist generated!');
        fetchJob();
      } else {
        toast.error(data.detail || 'Failed to generate checklist');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setGeneratingChecklist(false);
    }
  };

  const getCurrentStageIndex = () => {
    if (!job) return 0;
    const stages = getStagesForJob(job.job_type || 'walk_in');
    const normalizedStatus = normalizeStatus(job.status);
    const index = stages.findIndex(s => s.key === normalizedStatus);
    return index >= 0 ? index : 0;
  };

  const getNextStage = () => {
    if (!job) return null;
    const stages = getStagesForJob(job.job_type || 'walk_in');
    const currentIndex = getCurrentStageIndex();
    if (currentIndex < stages.length - 1) {
      return stages[currentIndex + 1];
    }
    return null;
  };

  const getJobStages = () => {
    return getStagesForJob(job?.job_type || 'walk_in');
  };

  const getStatusColor = (status) => {
    const colors = {
      job_created: 'bg-blue-500',
      driver_assigned: 'bg-blue-400',
      pickup_scheduled: 'bg-blue-400',
      picked_up: 'bg-blue-500',
      reached_garage: 'bg-indigo-500',
      ongoing: 'bg-purple-500',
      pending_estimate: 'bg-purple-500',
      quote_raised: 'bg-yellow-500',
      estimate_sent: 'bg-yellow-500',
      quote_approved: 'bg-green-500',
      customer_approved: 'bg-green-500',
      customer_partial_approved: 'bg-amber-500',
      work_in_progress: 'bg-orange-500',
      ready_for_delivery: 'bg-cyan-500',
      drop_scheduled: 'bg-teal-500',
      dropped: 'bg-emerald-600',
      delivered: 'bg-emerald-600',
      closed: 'bg-slate-600',
      cancelled: 'bg-gray-500'
    };
    return colors[status] || 'bg-slate-500';
  };

  const getStatusLabel = (status) => {
    // First check all stage arrays
    const allStages = [...WALKIN_STAGES, ...PICKUP_STAGES];
    const stage = allStages.find(s => s.key === status);
    if (stage) return stage.label;
    
    // Handle special cases
    const labels = {
      'customer_approved': 'Quote Approved',
      'customer_partial_approved': 'Partial Approved',
      'estimate_sent': 'Quote Raised',
      'pending_estimate': 'Ongoing',
      'drop_scheduled': 'Delivered',
      'dropped': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || status;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!job) return null;

  const nextStage = getNextStage();
  const currentStageIndex = getCurrentStageIndex();
  const jobStages = getJobStages();
  const isCancelled = job.status === 'cancelled';
  const isClosed = job.status === 'closed';
  const isReadOnly = isCancelled || isClosed;
  const isReadyForDelivery = job.status === 'ready_for_delivery';

  return (
    <div data-testid="job-detail-page" className={`min-h-screen ${theme.pageBg} transition-colors duration-200 overflow-x-hidden`}>
      {/* Cancelled Banner */}
      {isCancelled && (
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center gap-3 text-gray-300">
            <AlertCircle className="w-5 h-5 text-gray-400" />
            <span className="font-medium">This job has been cancelled and is now read-only. No further changes can be made.</span>
          </div>
        </div>
      )}
      
      {/* Closed Banner */}
      {isClosed && (
        <div className="bg-emerald-900/50 border-b border-emerald-700 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center gap-3 text-emerald-200">
            <Lock className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">This job has been closed and completed. Feedback request has been sent to the customer.</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className={`${theme.cardBg} border-b ${theme.border} px-4 py-3`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/garage/jobs')}
              className={`${theme.textSecondary} hover:${theme.textPrimary}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className={`text-lg font-bold ${theme.textPrimary}`}>{job.job_number}</h1>
                <Badge className={`${getStatusColor(job.status)} text-white`}>
                  {getStatusLabel(job.status)}
                </Badge>
              </div>
              <p className={`text-xs ${theme.textSecondary}`}>
                {job.source === 'propitstop' ? 'Propitstop Order' : 'Own Order'}
                {job.propitstop_order_id && ` • Order: ${job.propitstop_order_id}`}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        
        {/* Stage Progress & Update Card */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`${theme.textPrimary} text-lg flex items-center gap-2`}>
              <ClipboardList className="w-5 h-5 text-orange-500" />
              Job Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Stage Display */}
            <div className={`flex items-center justify-between p-4 ${theme.pageBg} rounded-lg`}>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${getStatusColor(job.status)} flex items-center justify-center`}>
                  {(() => {
                    const normalizedStatus = normalizeStatus(job.status);
                    const StageIcon = jobStages.find(s => s.key === normalizedStatus)?.icon || Car;
                    return <StageIcon className="w-6 h-6 text-white" />;
                  })()}
                </div>
                <div>
                  <p className={`text-sm ${theme.textSecondary}`}>Current Stage</p>
                  <p className={`text-xl font-bold ${theme.textPrimary}`}>{getStatusLabel(job.status)}</p>
                  {job.job_type && (
                    <Badge variant="outline" className={`text-xs mt-1 ${job.job_type === 'pickup' ? 'border-blue-500 text-blue-400' : 'border-green-500 text-green-400'}`}>
                      {job.job_type === 'pickup' ? 'Pickup Job' : 'Walk-in'}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm ${theme.textSecondary}`}>Progress</p>
                <p className="text-lg font-bold text-orange-400">
                  {currentStageIndex + 1} / {jobStages.length}
                </p>
              </div>
            </div>

            {/* Stage Progress Bar */}
            <div className="relative">
              {/* Stage dots - hidden on very small screens, shown on sm+ */}
              <div className="hidden sm:flex justify-between mb-2">
                {jobStages.map((stage, idx) => (
                  <div 
                    key={stage.key}
                    className={`w-3 h-3 rounded-full ${
                      idx <= currentStageIndex ? getStatusColor(stage.key) : theme.border.replace('border', 'bg')
                    }`}
                    title={stage.label}
                  />
                ))}
              </div>
              <div className={`h-1 ${theme.border.replace('border', 'bg')} rounded-full`}>
                <div 
                  className="h-full bg-gradient-to-r from-orange-500 to-green-500 rounded-full transition-all"
                  style={{ width: `${((currentStageIndex + 1) / jobStages.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Quick Action - Move to Next Stage */}
            {nextStage && job.status !== 'dropped' && job.status !== 'delivered' && job.status !== 'closed' && !isReadOnly && !isReadyForDelivery && (
              <div className={`flex items-center gap-3 p-3 ${theme.accentBg} border rounded-lg`}>
                <div className="flex-1">
                  <p className={`text-sm ${theme.textSecondary}`}>Next Stage</p>
                  <p className={`${theme.textPrimary} font-medium flex items-center gap-2`}>
                    {nextStage.label}
                    <ArrowRight className="w-4 h-4 text-orange-400" />
                  </p>
                </div>
                <Button
                  onClick={() => handleStatusUpdate(nextStage.key)}
                  disabled={updatingStatus}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="next-stage-btn"
                >
                  {updatingStatus ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Move to Next <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Delivery Method Choice - Show at "Ready for Delivery" stage */}
            {isReadyForDelivery && !isReadOnly && (
              <div className={`p-4 border rounded-lg ${theme.cardBg} border-cyan-500/30 space-y-4`}>
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-cyan-400" />
                  <p className={`${theme.textPrimary} font-semibold`}>Vehicle is Ready for Delivery</p>
                </div>
                <p className={`text-sm ${theme.textSecondary}`}>
                  How will the customer receive their vehicle?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setDeliveryMethod('self_pickup');
                      handleStatusUpdate('delivered');
                    }}
                    disabled={updatingStatus}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      deliveryMethod === 'self_pickup' 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-slate-600 hover:border-green-500/50'
                    }`}
                  >
                    <User className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    <p className={`font-medium ${theme.textPrimary}`}>Customer Pickup</p>
                    <p className={`text-xs ${theme.textSecondary} mt-1`}>Customer will pick up vehicle</p>
                  </button>
                  <button
                    onClick={() => setDeliveryMethod('need_drop')}
                    disabled={updatingStatus}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      deliveryMethod === 'need_drop' 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-slate-600 hover:border-blue-500/50'
                    }`}
                  >
                    <Truck className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                    <p className={`font-medium ${theme.textPrimary}`}>Need Drop</p>
                    <p className={`text-xs ${theme.textSecondary} mt-1`}>Assign driver for delivery</p>
                  </button>
                </div>
                
                {/* Driver Assignment for Drop */}
                {deliveryMethod === 'need_drop' && (
                  <div className={`mt-4 p-4 ${theme.pageBg} rounded-lg space-y-3`}>
                    <p className={`text-sm font-medium ${theme.textPrimary}`}>Assign Delivery Driver</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Driver *</label>
                        <Select 
                          value={dropForm.driver_id}
                          onValueChange={(v) => setDropForm({...dropForm, driver_id: v})}
                        >
                          <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                            <SelectValue placeholder="Select driver" />
                          </SelectTrigger>
                          <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                            {drivers.map(d => (
                              <SelectItem key={d.id} value={d.id}>{d.name} - {d.phone}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Date *</label>
                        <input 
                          type="date"
                          value={dropForm.scheduled_date}
                          onChange={(e) => setDropForm({...dropForm, scheduled_date: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full px-3 py-2 rounded-md ${theme.inputBg} ${theme.border} ${theme.textPrimary}`}
                        />
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Time Slot *</label>
                        <Select 
                          value={dropForm.time_slot}
                          onValueChange={(v) => setDropForm({...dropForm, time_slot: v})}
                        >
                          <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                            {timeSlots.map(slot => (
                              <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button 
                      onClick={handleAssignDrop}
                      disabled={assigningDrop || !dropForm.driver_id || !dropForm.scheduled_date || !dropForm.time_slot}
                      className="w-full bg-blue-600 hover:bg-blue-700 mt-2"
                    >
                      {assigningDrop ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Truck className="w-4 h-4 mr-2" />}
                      Assign Driver for Drop
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Close Job Action - Show when job is delivered and payment received */}
            {(job.status === 'dropped' || job.status === 'delivered') && !isClosed && (
              <div className={`flex items-center gap-3 p-3 ${job.payment_received ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'} border rounded-lg`}>
                <div className="flex-1">
                  <p className={`text-sm ${job.payment_received ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {job.payment_received ? 'Ready to Close' : 'Payment Required'}
                  </p>
                  <p className={`${theme.textPrimary} font-medium flex items-center gap-2`}>
                    {job.payment_received 
                      ? 'Close job to complete and send feedback request' 
                      : 'Record payment before closing the job'}
                  </p>
                </div>
                <Button
                  onClick={handleCloseJob}
                  disabled={closingJob || !job.payment_received}
                  className={job.payment_received 
                    ? "bg-emerald-600 hover:bg-emerald-700" 
                    : "bg-gray-600 cursor-not-allowed"}
                  data-testid="close-job-btn"
                >
                  {closingJob ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-1" />
                      Close Job
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Manual Stage Selection - Hidden for readonly jobs */}
            {!isReadOnly && (
            <div className="flex items-center gap-3">
              <Select 
                value={normalizeStatus(job.status)} 
                onValueChange={(value) => handleStatusUpdate(value)}
                disabled={updatingStatus}
              >
                <SelectTrigger className={`flex-1 ${theme.inputBg} ${theme.textPrimary}`}>
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                  {jobStages.filter(s => s.key !== 'closed').map((stage) => (
                    <SelectItem 
                      key={stage.key} 
                      value={stage.key}
                      className={`${theme.textPrimary} ${theme.hoverBg}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(stage.key)}`} />
                        {stage.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => handleStatusUpdate('cancelled')}
                disabled={updatingStatus || job.status === 'cancelled'}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                Cancel Job
              </Button>
            </div>
            )}

            {/* Backward Move Confirmation Modal */}
            {showBackwardConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className={`${theme.cardBgSolid} border ${theme.border} rounded-lg p-6 max-w-md mx-4`}>
                  <div className="flex items-center gap-3 mb-4">
                    <AlertCircle className="w-6 h-6 text-amber-500" />
                    <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Confirm Backward Move</h3>
                  </div>
                  <p className={`${theme.textSecondary} mb-6`}>
                    Moving to a previous stage requires manual override. Are you sure you want to move this job backward to "{getStatusLabel(pendingBackwardStatus)}"?
                  </p>
                  <div className="flex gap-3 justify-end">
                    <Button
                      variant="outline"
                      onClick={handleCancelBackward}
                      className={`${theme.border} ${theme.textSecondary}`}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmBackward}
                      className="bg-amber-600 hover:bg-amber-700"
                      disabled={updatingStatus}
                    >
                      {updatingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Confirm Override
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer & Vehicle Info */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className={`${theme.cardBg} border ${theme.border}`}>
            <CardHeader className="py-3 flex flex-row items-center justify-between">
              <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                <User className="w-4 h-4" /> Customer
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={openEditCustomer}
                data-testid="edit-customer-btn"
                className={`h-7 px-2 text-xs ${theme.textSecondary} hover:${theme.textPrimary}`}
              >
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              <p className={`${theme.textPrimary} font-medium`}>{job.customer_name}</p>
              <p className={`${theme.textSecondary} text-sm flex items-center gap-1`}>
                <Phone className="w-3 h-3" /> {job.customer_mobile}
              </p>
              {job.customer_email && (
                <p className={`${theme.textSecondary} text-sm`}>{job.customer_email}</p>
              )}
            </CardContent>
          </Card>

          <Card className={`${theme.cardBg} border ${theme.border}`}>
            <CardHeader className="py-3">
              <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                <Car className="w-4 h-4" /> Vehicle
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className={`${theme.textPrimary} font-medium`}>{job.vehicle_number || 'No plate'}</p>
              <p className={`${theme.textSecondary} text-sm`}>
                {job.vehicle_make} {job.vehicle_model} {job.vehicle_year && `(${job.vehicle_year})`}
              </p>
              {job.vehicle_color && (
                <p className={`${theme.textSecondary} text-sm`}>Color: {job.vehicle_color}</p>
              )}
              {job.odometer_reading && (
                <p className={`${theme.textSecondary} text-sm`}>Odometer: {job.odometer_reading} km</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className={`w-full ${theme.cardBg} flex flex-wrap sm:grid sm:grid-cols-7 gap-1`}>
            <TabsTrigger value="overview" className="data-[state=active]:bg-orange-600 text-xs flex-1 sm:flex-none min-w-[60px]">Overview</TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-orange-600 text-xs flex-1 sm:flex-none min-w-[55px]">Images</TabsTrigger>
            <TabsTrigger value="checklist" className="data-[state=active]:bg-orange-600 text-xs flex-1 sm:flex-none min-w-[65px]">Checklist</TabsTrigger>
            <TabsTrigger value="estimate" className="data-[state=active]:bg-orange-600 text-xs flex-1 sm:flex-none min-w-[60px]">Estimate</TabsTrigger>
            <TabsTrigger value="wip" className="data-[state=active]:bg-orange-600 text-xs flex-1 sm:flex-none min-w-[40px]">WIP</TabsTrigger>
            <TabsTrigger value="pickup-drop" className="data-[state=active]:bg-orange-600 text-xs flex-1 sm:flex-none min-w-[80px]">Pickup/Drop</TabsTrigger>
            <TabsTrigger value="invoice" className="data-[state=active]:bg-orange-600 text-xs flex-1 sm:flex-none min-w-[50px]">Invoice</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Customer Complaint */}
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3">
                <CardTitle className={`${theme.textPrimary} text-sm`}>Customer Complaint</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {job.customer_complaint?.text ? (
                  <p className={theme.textSecondary}>{job.customer_complaint.text}</p>
                ) : (
                  <p className={`${theme.textMuted} italic`}>No complaint recorded</p>
                )}
              </CardContent>
            </Card>

            {/* AI Extraction */}
            {job.ai_vehicle_extraction && (
              <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-500/30">
                <CardHeader className="py-3">
                  <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                    <Sparkles className="w-4 h-4 text-purple-400" /> AI Detection
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {job.ai_vehicle_extraction.visible_damages?.length > 0 && (
                    <div>
                      <p className={`${theme.textSecondary} text-sm mb-1`}>Visible Damages:</p>
                      <ul className="space-y-1">
                        {job.ai_vehicle_extraction.visible_damages.map((d, i) => (
                          <li key={i} className="text-red-400 text-sm flex items-start gap-2">
                            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className={`text-xs ${theme.textMuted} mt-2`}>
                    Confidence: {Math.round((job.ai_vehicle_extraction.confidence_score || 0) * 100)}%
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Status History */}
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <Calendar className="w-4 h-4" /> Status History
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {job.status_history?.slice().reverse().map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(entry.status)}`} />
                      <span className={theme.textPrimary}>{getStatusLabel(entry.status)}</span>
                      <span className={`${theme.textMuted} text-xs`}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                      {entry.notes && (
                        <span className={`${theme.textSecondary} text-xs`}>- {entry.notes}</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images" className="mt-4">
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <Camera className="w-4 h-4" /> Vehicle Images ({job.images?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {job.images?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {job.images.map((img, idx) => (
                      <div key={idx} className={`relative aspect-square rounded-lg overflow-hidden border ${theme.border}`}>
                        <img 
                          src={`${API_URL}/api${img.url}`} 
                          alt={img.image_type}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to direct URL if /api/uploads fails
                            if (!e.target.dataset.fallback) {
                              e.target.dataset.fallback = 'true';
                              e.target.src = `${API_URL}${img.url}`;
                            }
                          }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                          <span className="text-white text-xs capitalize">{img.image_type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`${theme.textMuted} text-center py-8`}>No images uploaded</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Checklist Tab */}
          <TabsContent value="checklist" className="mt-4">
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <ClipboardList className="w-4 h-4" /> Inspection Checklist
                </CardTitle>
                {job.checklist?.length === 0 && !isReadOnly && (
                  <Button 
                    onClick={handleGenerateChecklist}
                    size="sm"
                    className="bg-gradient-to-r from-purple-500 to-pink-600"
                    disabled={generatingChecklist}
                    data-testid="generate-checklist-btn"
                  >
                    {generatingChecklist ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1" />
                    )}
                    Generate with AI
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {job.checklist?.length > 0 ? (
                  <div className="space-y-3">
                    {job.checklist.map((item, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-start gap-3 p-3 ${theme.pageBg} rounded-lg`}
                      >
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          item.status === 'ok' ? 'bg-green-500' :
                          item.status === 'needs_attention' ? 'bg-yellow-500' :
                          item.status === 'replaced' ? 'bg-blue-500' : 'bg-slate-500'
                        }`}>
                          {item.status === 'ok' && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-xs ${theme.border} ${theme.textSecondary}`}>
                              {item.category}
                            </Badge>
                          </div>
                          <p className={`${theme.textPrimary} mt-1`}>{item.item}</p>
                          {item.notes && (
                            <p className={`${theme.textSecondary} text-sm mt-1`}>{item.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ClipboardList className={`w-12 h-12 mx-auto mb-2 ${theme.textMuted}`} />
                    <p className={theme.textSecondary}>No checklist generated yet</p>
                    <p className={`${theme.textMuted} text-sm mt-1`}>
                      Add a customer complaint first, then generate AI checklist
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Estimate Tab */}
          <TabsContent value="estimate" className="mt-4">
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <FileText className="w-4 h-4" /> Estimate / Quotation
                </CardTitle>
                {!isReadOnly && (
                <div className="flex gap-2">
                  {job.estimate && (
                    <Button 
                      onClick={() => navigate(`/garage/jobs/${jobId}/estimate`)}
                      size="sm"
                      variant="outline"
                      className={`${theme.border} ${theme.textSecondary}`}
                    >
                      Edit Estimate
                    </Button>
                  )}
                  {!job.estimate && (
                    <Button 
                      onClick={() => navigate(`/garage/jobs/${jobId}/estimate`)}
                      size="sm"
                      className="bg-gradient-to-r from-orange-500 to-red-600"
                    >
                      Create Estimate
                    </Button>
                  )}
                </div>
                )}
              </CardHeader>
              <CardContent>
                {job.estimate ? (
                  <div className="space-y-4">
                    {/* Estimate Status */}
                    {job.estimate.sent_to_customer_at && (
                      <div className={`p-3 ${theme.infoBg} border rounded-lg`}>
                        <p className={`text-sm ${theme.infoText}`}>
                          ✉️ Sent to customer on {new Date(job.estimate.sent_to_customer_at).toLocaleDateString()}
                        </p>
                        {job.estimate.customer_response_at && (
                          <p className={`text-sm ${theme.successText} mt-1`}>
                            ✅ Customer responded on {new Date(job.estimate.customer_response_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Line Items */}
                    <div className="space-y-2">
                      {job.estimate.line_items?.map((item, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 ${theme.pageBg} rounded-lg`}>
                          <div>
                            <p className={theme.textPrimary}>{item.part_name || item.description}</p>
                            <p className={`${theme.textSecondary} text-sm`}>
                              {item.item_type === 'part' ? 'Part' : item.item_type === 'body_shop_labour' ? 'Body Shop' : 'Labour'} × {item.quantity}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`${theme.textPrimary} font-medium`}>₹{item.total_amount?.toLocaleString()}</p>
                            <Badge className={`text-xs ${
                              item.status === 'approved' ? 'bg-green-500' :
                              item.status === 'rejected' ? 'bg-red-500' : 'bg-slate-500'
                            }`}>
                              {item.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Totals */}
                    <div className={`border-t ${theme.border} pt-3 space-y-1`}>
                      <div className="flex justify-between text-sm">
                        <span className={theme.textSecondary}>Parts</span>
                        <span className={theme.textPrimary}>₹{job.estimate.subtotal_parts?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className={theme.textSecondary}>Labour</span>
                        <span className={theme.textPrimary}>₹{job.estimate.subtotal_labour?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className={theme.textSecondary}>GST</span>
                        <span className={theme.textPrimary}>₹{job.estimate.total_gst?.toLocaleString()}</span>
                      </div>
                      <div className={`flex justify-between text-lg font-bold border-t ${theme.border} pt-2`}>
                        <span className={theme.textPrimary}>Total</span>
                        <span className="text-orange-400">₹{job.estimate.grand_total?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className={`w-12 h-12 mx-auto mb-2 ${theme.textMuted}`} />
                    <p className={theme.textSecondary}>No estimate created yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Work In Progress Tab */}
          <TabsContent value="wip" className="mt-4">
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <Wrench className="w-4 h-4" /> Work In Progress
                </CardTitle>
                {!isReadOnly && (
                <Button 
                  onClick={() => navigate(`/garage/jobs/${jobId}/wip`)}
                  size="sm"
                  className="bg-gradient-to-r from-orange-500 to-red-600"
                  disabled={!job.estimate || !['quote_approved', 'customer_approved', 'customer_partial_approved', 'work_in_progress', 'qc_pending', 'qc_passed', 'qc_failed', 'ready_for_delivery', 'delivered'].includes(job.status)}
                >
                  {job.wip_proofs?.length > 0 ? 'View Work' : 'Start Work'}
                </Button>
                )}
              </CardHeader>
              <CardContent>
                {!job.estimate ? (
                  <p className={`${theme.textMuted} text-sm text-center py-4`}>
                    Create estimate first to start work
                  </p>
                ) : !['quote_approved', 'customer_approved', 'customer_partial_approved', 'work_in_progress', 'qc_pending', 'qc_passed', 'qc_failed', 'ready_for_delivery', 'delivered'].includes(job.status) ? (
                  <p className={`${theme.textMuted} text-sm text-center py-4`}>
                    Waiting for customer approval on estimate
                  </p>
                ) : job.wip_proofs && job.wip_proofs.length > 0 ? (
                  <div className="space-y-3">
                    <div className={`flex items-center justify-between p-3 ${theme.pageBg} rounded-lg`}>
                      <span className={theme.textSecondary}>Parts with new photos</span>
                      <span className={`${theme.textPrimary} font-medium`}>{job.wip_proofs.length}</span>
                    </div>
                    <div className="space-y-2">
                      {job.wip_proofs.slice(0, 3).map((proof, idx) => (
                        <div key={idx} className={`flex items-center gap-3 p-2 ${theme.successBg} rounded-lg border`}>
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <div className="flex-1">
                            <p className={`${theme.textPrimary} text-sm`}>{proof.description}</p>
                            <p className={`${theme.textSecondary} text-xs`}>{proof.new_part_images?.length || 0} photos</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Wrench className={`w-10 h-10 ${theme.textMuted} mx-auto mb-2`} />
                    <p className={`${theme.textMuted} text-sm`}>Upload new part photos as you install them</p>
                    <p className={`${theme.textMuted} text-sm mt-1`}>Complete work to proceed to Quality Check</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pickup/Drop Tab */}
          <TabsContent value="pickup-drop" className="mt-4 space-y-4">
            {/* Pickup Section */}
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <Truck className="w-4 h-4 text-green-500" /> Vehicle Pickup
                </CardTitle>
              </CardHeader>
              <CardContent>
                {job.pickup_info ? (
                  <div className={`p-4 ${theme.successBg} border border-green-500/30 rounded-lg`}>
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-green-500">Pickup Assigned</Badge>
                      <span className={`text-sm ${theme.textSecondary}`}>
                        {job.pickup_info.scheduled_date}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className={theme.textSecondary}>Driver</p>
                        <p className={`${theme.textPrimary} font-medium`}>{job.pickup_info.driver_name}</p>
                      </div>
                      <div>
                        <p className={theme.textSecondary}>Phone</p>
                        <p className={`${theme.textPrimary} font-medium`}>{job.pickup_info.driver_phone}</p>
                      </div>
                      <div>
                        <p className={theme.textSecondary}>Time Slot</p>
                        <p className={`${theme.textPrimary} font-medium`}>{job.pickup_info.time_slot}</p>
                      </div>
                      <div>
                        <p className={theme.textSecondary}>Status</p>
                        <Badge variant="outline" className="text-green-500 border-green-500/50">
                          {job.pickup_info.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className={`${theme.textMuted} text-sm`}>Assign a driver for vehicle pickup</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Driver *</label>
                        <Select 
                          value={pickupForm.driver_id}
                          onValueChange={(v) => setPickupForm({...pickupForm, driver_id: v})}
                        >
                          <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                            <SelectValue placeholder="Select driver" />
                          </SelectTrigger>
                          <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                            {drivers.map(d => (
                              <SelectItem key={d.id} value={d.id}>{d.name} - {d.phone}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Date *</label>
                        <input 
                          type="date"
                          value={pickupForm.scheduled_date}
                          onChange={(e) => setPickupForm({...pickupForm, scheduled_date: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full px-3 py-2 rounded-md ${theme.inputBg} ${theme.border} ${theme.textPrimary}`}
                        />
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Time Slot *</label>
                        <Select 
                          value={pickupForm.time_slot}
                          onValueChange={(v) => setPickupForm({...pickupForm, time_slot: v})}
                        >
                          <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                            {timeSlots.map(slot => (
                              <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Notes</label>
                        <input 
                          type="text"
                          value={pickupForm.notes}
                          onChange={(e) => setPickupForm({...pickupForm, notes: e.target.value})}
                          placeholder="Any special instructions"
                          className={`w-full px-3 py-2 rounded-md ${theme.inputBg} ${theme.border} ${theme.textPrimary}`}
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={handleAssignPickup}
                      disabled={assigningPickup || !pickupForm.driver_id}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="assign-pickup-btn"
                    >
                      {assigningPickup ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Truck className="w-4 h-4 mr-2" />}
                      Assign Driver for Pickup
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Drop/Delivery Section */}
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <MapPin className="w-4 h-4 text-blue-500" /> Vehicle Delivery
                </CardTitle>
              </CardHeader>
              <CardContent>
                {job.drop_info ? (
                  <div className={`p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg`}>
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-blue-500">Delivery Assigned</Badge>
                      <span className={`text-sm ${theme.textSecondary}`}>
                        {job.drop_info.scheduled_date}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className={theme.textSecondary}>Driver</p>
                        <p className={`${theme.textPrimary} font-medium`}>{job.drop_info.driver_name}</p>
                      </div>
                      <div>
                        <p className={theme.textSecondary}>Phone</p>
                        <p className={`${theme.textPrimary} font-medium`}>{job.drop_info.driver_phone}</p>
                      </div>
                      <div>
                        <p className={theme.textSecondary}>Time Slot</p>
                        <p className={`${theme.textPrimary} font-medium`}>{job.drop_info.time_slot}</p>
                      </div>
                      <div>
                        <p className={theme.textSecondary}>Status</p>
                        <Badge variant="outline" className="text-blue-500 border-blue-500/50">
                          {job.drop_info.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className={`${theme.textMuted} text-sm`}>Assign a driver for vehicle delivery</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Driver *</label>
                        <Select 
                          value={dropForm.driver_id}
                          onValueChange={(v) => setDropForm({...dropForm, driver_id: v})}
                        >
                          <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                            <SelectValue placeholder="Select driver" />
                          </SelectTrigger>
                          <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                            {drivers.map(d => (
                              <SelectItem key={d.id} value={d.id}>{d.name} - {d.phone}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Date *</label>
                        <input 
                          type="date"
                          value={dropForm.scheduled_date}
                          onChange={(e) => setDropForm({...dropForm, scheduled_date: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full px-3 py-2 rounded-md ${theme.inputBg} ${theme.border} ${theme.textPrimary}`}
                        />
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Time Slot *</label>
                        <Select 
                          value={dropForm.time_slot}
                          onValueChange={(v) => setDropForm({...dropForm, time_slot: v})}
                        >
                          <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                            {timeSlots.map(slot => (
                              <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className={`text-xs ${theme.textSecondary}`}>Notes</label>
                        <input 
                          type="text"
                          value={dropForm.notes}
                          onChange={(e) => setDropForm({...dropForm, notes: e.target.value})}
                          placeholder="Any special instructions"
                          className={`w-full px-3 py-2 rounded-md ${theme.inputBg} ${theme.border} ${theme.textPrimary}`}
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={handleAssignDrop}
                      disabled={assigningDrop || !dropForm.driver_id}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid="assign-drop-btn"
                    >
                      {assigningDrop ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
                      Assign Driver for Delivery
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* No drivers notice */}
            {drivers.length === 0 && (
              <div className={`p-4 ${theme.cardBg} border ${theme.border} rounded-lg text-center`}>
                <User className={`w-8 h-8 mx-auto ${theme.textMuted} mb-2`} />
                <p className={theme.textSecondary}>No drivers added yet</p>
                <Button 
                  onClick={() => navigate('/garage/drivers')}
                  variant="link"
                  className="text-orange-500"
                >
                  Add your first driver
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Invoice Tab */}
          <TabsContent value="invoice" className="mt-4">
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <FileText className="w-4 h-4" /> Invoice
                </CardTitle>
                {!isReadOnly && (
                <>
                {job.invoice_id ? (
                  <Button 
                    onClick={() => navigate(`/garage/jobs/${jobId}/invoice/${job.invoice_id}`)}
                    size="sm"
                    className="bg-gradient-to-r from-orange-500 to-red-600"
                    data-testid="view-invoice-btn"
                  >
                    View Invoice
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate(`/garage/jobs/${jobId}/invoice/generate`)}
                    size="sm"
                    className="bg-gradient-to-r from-green-500 to-emerald-600"
                    disabled={!['ready_for_delivery', 'delivered'].includes(job.status)}
                    data-testid="generate-invoice-btn"
                  >
                    Generate Invoice
                  </Button>
                )}
                </>
                )}
              </CardHeader>
              <CardContent>
                {job.invoice_id ? (
                  <div className="space-y-3">
                    <div className={`flex items-center justify-between p-3 ${theme.successBg} border rounded-lg`}>
                      <span className={theme.successText}>Invoice Generated</span>
                      <span className={`${theme.textPrimary} text-sm`}>
                        {job.invoice_generated_at && new Date(job.invoice_generated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className={`w-12 h-12 mx-auto mb-2 ${theme.textMuted}`} />
                    <p className={theme.textSecondary}>Invoice not generated yet</p>
                    {!['ready_for_delivery', 'delivered'].includes(job.status) ? (
                      <p className={`${theme.textMuted} text-sm mt-1`}>Complete Work In Progress to generate invoice</p>
                    ) : (
                      <p className={`text-green-500 text-sm mt-1`}>Work complete! Click "Generate Invoice" to proceed</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Customer Modal */}
      {editCustomerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !savingCustomer && setEditCustomerOpen(false)}
          data-testid="edit-customer-modal"
        >
          <div
            className={`w-full max-w-md ${theme.cardBg} border ${theme.border} rounded-lg shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-4 border-b ${theme.border} flex items-center justify-between`}>
              <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Edit Customer</h3>
              <button
                onClick={() => !savingCustomer && setEditCustomerOpen(false)}
                className={`${theme.textSecondary} hover:${theme.textPrimary} text-xl leading-none`}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${theme.textSecondary}`}>Name *</label>
                <input
                  type="text"
                  value={customerForm.customer_name}
                  onChange={(e) => setCustomerForm({ ...customerForm, customer_name: e.target.value })}
                  className={`w-full px-3 py-2 rounded border ${theme.border} ${theme.cardBg} ${theme.textPrimary} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  data-testid="edit-customer-name"
                />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${theme.textSecondary}`}>Mobile *</label>
                <input
                  type="tel"
                  value={customerForm.customer_mobile}
                  onChange={(e) => setCustomerForm({ ...customerForm, customer_mobile: e.target.value })}
                  className={`w-full px-3 py-2 rounded border ${theme.border} ${theme.cardBg} ${theme.textPrimary} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  data-testid="edit-customer-mobile"
                />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${theme.textSecondary}`}>Email</label>
                <input
                  type="email"
                  value={customerForm.customer_email}
                  onChange={(e) => setCustomerForm({ ...customerForm, customer_email: e.target.value })}
                  className={`w-full px-3 py-2 rounded border ${theme.border} ${theme.cardBg} ${theme.textPrimary} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  data-testid="edit-customer-email"
                />
              </div>
              <p className={`text-xs ${theme.textSecondary} pt-1`}>
                Changes apply to this job and update the master customer record (visible in other jobs and invoices).
              </p>
            </div>
            <div className={`px-5 py-3 border-t ${theme.border} flex justify-end gap-2`}>
              <Button
                variant="ghost"
                onClick={() => setEditCustomerOpen(false)}
                disabled={savingCustomer}
              >
                Cancel
              </Button>
              <Button
                onClick={saveCustomer}
                disabled={savingCustomer}
                data-testid="edit-customer-save"
              >
                {savingCustomer ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
